import { useCallback, useEffect, useRef } from "react";
import { inboxApi } from "../api/api";

/**
 * Returns an async function that yields a conversation id. If we already
 * have one (`conversationId` set), it resolves to that immediately. If we
 * only have a `draftContactId`, it calls the find-or-create endpoint once
 * and memoises the in-flight promise so a fast double-click on Send does
 * not produce two POSTs (which would race on the unique constraint).
 *
 * Memoisation lives on a ref — not on `useState` — so it survives the
 * synchronous re-render that follows the first send without releasing
 * the in-flight promise.
 */
export const useEnsureConversation = (
  conversationId: string | undefined,
  draftContactId: number | undefined
) => {
  const inflight = useRef<Promise<string> | null>(null);

  // Drop the cached promise when the draft contact changes so a reused
  // (non-remounted) composer never resolves to the previous contact's id.
  useEffect(() => {
    inflight.current = null;
  }, [draftContactId]);

  return useCallback(async (): Promise<string> => {
    if (conversationId) return conversationId;
    if (draftContactId === undefined)
      throw new Error("useEnsureConversation: no conversation or contact");

    if (inflight.current) return inflight.current;

    inflight.current = inboxApi
      .createConversation({ contact_id: draftContactId })
      .then((r) => {
        if (!r.conversation.id)
          throw new Error("createConversation returned no id");
        return r.conversation.id;
      })
      .catch((e) => {
        // Release the cached promise on failure so the next click can retry.
        inflight.current = null;
        throw e;
      });

    return inflight.current;
  }, [conversationId, draftContactId]);
};
